import { useMemo } from "react";
import type { ViewOptions } from "@common/types/views/views";
import { type WorkBoardLaneBy, workBoardLaneBySchema } from "./workBoardLanes";

/** The board settings a Work view keeps, in either layout. */
export interface WorkViewBoardDisplay {
    /** The swimlanes; null is none. */
    laneBy: WorkBoardLaneBy | null;
    isEmptyColumnsShown: boolean;
}

const DEFAULT_DISPLAY: WorkViewBoardDisplay = {
    laneBy: null,
    isEmptyColumnsShown: true,
};

/**
 * The board settings a view opens with. A view saved before Board has
 * none, and swimlanes the page doesn't offer fall back to none.
 */
export const getWorkViewBoardDisplay = (
    options: ViewOptions | null | undefined,
): WorkViewBoardDisplay => ({
    laneBy: workBoardLaneBySchema.parse(
        options?.board?.laneBy ?? DEFAULT_DISPLAY.laneBy,
    ),
    isEmptyColumnsShown:
        options?.board?.isEmptyColumnsShown ??
        DEFAULT_DISPLAY.isEmptyColumnsShown,
});

/**
 * A view's options with a board settings change. Defaults are left out, so
 * returning to them matches a view saved before Board and the view no
 * longer shows unsaved changes; a view without options stays without them.
 * The view's other board settings, such as a Kick view's card tags, stay.
 */
export const withWorkViewBoardDisplay = ({
    options,
    change,
}: {
    options: ViewOptions | null | undefined;
    change: Partial<WorkViewBoardDisplay>;
}): ViewOptions | null | undefined => {
    const { laneBy, isEmptyColumnsShown } = {
        ...getWorkViewBoardDisplay(options),
        ...change,
    };
    const { board: storedBoard, ...otherOptions } = options ?? {};
    const {
        laneBy: _laneBy,
        isEmptyColumnsShown: _isEmptyColumnsShown,
        ...otherBoard
    } = storedBoard ?? {};
    const board = {
        ...otherBoard,
        ...(laneBy !== DEFAULT_DISPLAY.laneBy && { laneBy }),
        ...(isEmptyColumnsShown !== DEFAULT_DISPLAY.isEmptyColumnsShown && {
            isEmptyColumnsShown,
        }),
    };
    const hasBoard = Object.keys(board).length > 0;
    if (!options && !hasBoard) {
        return options;
    }
    return { ...otherOptions, ...(hasBoard && { board }) };
};

/**
 * A view's own card tags, each tag once; undefined falls back to List's
 * columns.
 */
export const getWorkViewCardFieldIds = ({
    options,
}: {
    options: ViewOptions | null | undefined;
}) => {
    const cardFields = options?.board?.cardFields;
    return cardFields ? [...new Set(cardFields)] : undefined;
};

/**
 * `getWorkViewCardFieldIds` with one result per saved list, so callers can
 * use it as a memo dependency.
 */
export const useWorkViewCardFieldIds = ({
    options,
}: {
    options: ViewOptions | null | undefined;
}) => {
    const cardFields = options?.board?.cardFields;
    return useMemo(
        () => getWorkViewCardFieldIds({ options: { board: { cardFields } } }),
        [cardFields],
    );
};

/** A view's options with new card tags, its other board settings kept. */
export const withWorkViewCardFields = ({
    options,
    cardFields,
}: {
    options: ViewOptions | null | undefined;
    cardFields: string[];
}): ViewOptions => ({
    ...options,
    board: { ...options?.board, cardFields },
});
